import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import PublicHeader from '../components/PublicHeader';
import PublicFooter from '../components/PublicFooter';

var API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomerPortal() {
  var [step, setStep] = useState(1);
  var [loading, setLoading] = useState(false);
  var [portalEnabled, setPortalEnabled] = useState(true);
  var [checkingStatus, setCheckingStatus] = useState(true);
  var [leadId, setLeadId] = useState(null);
  var [form, setForm] = useState({ name: '', business_name: '', phone: '', email: '', nmi: '' });
  var [file, setFile] = useState(null);
  var [extractedData, setExtractedData] = useState(null);
  var [result, setResult] = useState(null);
  // Tariff/Plan selection
  var [tariffs, setTariffs] = useState([]);
  var [plans, setPlans] = useState([]);
  var [selectedTariff, setSelectedTariff] = useState(null);
  var [selectedPlan, setSelectedPlan] = useState(null);
  var [detectedState, setDetectedState] = useState('');

  useEffect(function() {
    axios.get(API_URL + '/api/portal/status').then(function(r) {
      setPortalEnabled(r.data.enabled !== false);
    }).catch(function() {}).finally(function() { setCheckingStatus(false); });
  }, []);

  function updateForm(field, value) { setForm(function(prev) { return Object.assign({}, prev, { [field]: value }); }); }

  function submitDetails() {
    if (!form.name || !form.email) { toast.error('Please enter your name and email'); return; }
    setLoading(true);
    var fd = new FormData();
    fd.append('name', form.name);
    fd.append('business_name', form.business_name);
    fd.append('phone', form.phone);
    fd.append('email', form.email);
    fd.append('nmi', form.nmi);
    axios.post(API_URL + '/api/public/leads', fd)
      .then(function(r) { setLeadId(r.data.lead_id); setStep(2); })
      .catch(function() { toast.error('Something went wrong'); })
      .finally(function() { setLoading(false); });
  }

  function uploadBill() {
    if (!file) { toast.error('Please select a file'); return; }
    setLoading(true);
    var fd = new FormData();
    fd.append('lead_id', leadId);
    fd.append('file', file);
    axios.post(API_URL + '/api/public/upload-bill', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(function(r) {
        setExtractedData(r.data.extracted_data);
        // Detect state from NMI or bill address and load tariffs
        var nmi = form.nmi || r.data.extracted_data?.nmi || '';
        var address = r.data.extracted_data?.site_address || '';
        return axios.get(API_URL + '/api/public/nmi-lookup?nmi=' + encodeURIComponent(nmi) + '&address=' + encodeURIComponent(address));
      })
      .then(function(r) {
        var st = r.data.state || '';
        setDetectedState(st);
        return axios.get(API_URL + '/api/public/tariffs-for-state?state=' + encodeURIComponent(st));
      })
      .then(function(r) {
        setTariffs(r.data.tariffs || []);
        if (r.data.tariffs && r.data.tariffs.length > 0) {
          setSelectedTariff(r.data.tariffs[0].id);
          return axios.get(API_URL + '/api/public/plans-for-tariff?tariff_id=' + r.data.tariffs[0].id);
        }
        return { data: { plans: [] } };
      })
      .then(function(r) {
        setPlans(r.data.plans || []);
        if (r.data.plans && r.data.plans.length > 0) setSelectedPlan(r.data.plans[0].id);
        setStep(3);
      })
      .catch(function(err) { toast.error(err.response?.data?.detail || 'Failed to process bill'); })
      .finally(function() { setLoading(false); });
  }

  function onTariffSelect(tariffId) {
    setSelectedTariff(tariffId);
    setSelectedPlan(null);
    axios.get(API_URL + '/api/public/plans-for-tariff?tariff_id=' + tariffId)
      .then(function(r) { setPlans(r.data.plans || []); if (r.data.plans.length > 0) setSelectedPlan(r.data.plans[0].id); });
  }

  function runComparison() {
    if (!selectedTariff || !selectedPlan) { toast.error('Please select a tariff and plan'); return; }
    setLoading(true);
    setStep(4);
    var fd = new FormData();
    fd.append('lead_id', leadId);
    fd.append('tariff_id', selectedTariff);
    fd.append('plan_id', selectedPlan);
    axios.post(API_URL + '/api/public/compare', fd)
      .then(function(r) { setResult(r.data); setStep(5); })
      .catch(function(err) { toast.error(err.response?.data?.detail || 'Could not generate comparison'); setStep(3); })
      .finally(function() { setLoading(false); });
  }

  var fmt = function(a) { return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(a || 0); };

  var steps = [{n:1,t:'Your Details'},{n:2,t:'Upload Bill'},{n:3,t:'Select Plan'},{n:4,t:'Processing'},{n:5,t:'Your Savings'}];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <PublicHeader />

      <div className="flex-1">
      {checkingStatus ? (
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : !portalEnabled ? (
        <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="portal-disabled-message">
          <div className="bg-white rounded-xl border shadow-sm p-10 max-w-md mx-auto">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Temporarily Unavailable</h2>
            <p className="text-slate-500">Our comparison tool is currently undergoing maintenance. Please check back shortly.</p>
            <p className="text-sm text-slate-400 mt-4">For urgent enquiries, contact us directly.</p>
          </div>
        </div>
      ) : (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-2 mb-8">
          {steps.map(function(s, idx) {
            var active = step >= s.n;
            return (
              <div key={s.n} className="flex items-center flex-1">
                <div className={'flex items-center gap-1.5 ' + (active ? 'text-primary' : 'text-slate-400')}>
                  <div className={'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ' + (step > s.n ? 'bg-primary text-white' : active ? 'bg-primary/10 text-primary border-2 border-primary' : 'bg-slate-200 text-slate-400')}>{step > s.n ? '\u2713' : s.n}</div>
                  <span className="text-xs font-medium hidden sm:inline">{s.t}</span>
                </div>
                {idx < steps.length - 1 && <div className={'flex-1 h-0.5 mx-1.5 ' + (step > s.n ? 'bg-primary' : 'bg-slate-200')} />}
              </div>
            );
          })}
        </div>

        {step === 1 && (
          <div className="bg-white rounded-xl border shadow-sm p-6 space-y-5" data-testid="customer-step-1">
            <div className="text-center mb-4">
              <h1 className="text-2xl font-bold text-slate-900">Compare Your Electricity Bill</h1>
              <p className="text-slate-500 mt-1">Enter your details below to get started</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Your Name *</label>
                <input value={form.name} onChange={function(e) { updateForm('name', e.target.value); }} className="w-full h-11 px-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="John Smith" data-testid="customer-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Business Name</label>
                <input value={form.business_name} onChange={function(e) { updateForm('business_name', e.target.value); }} className="w-full h-11 px-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="Your Business Pty Ltd" data-testid="customer-business" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                <input value={form.phone} onChange={function(e) { updateForm('phone', e.target.value); }} className="w-full h-11 px-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="04XX XXX XXX" data-testid="customer-phone" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                <input value={form.email} onChange={function(e) { updateForm('email', e.target.value); }} type="email" className="w-full h-11 px-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="you@business.com" data-testid="customer-email" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">NMI (National Metering Identifier)</label>
                <input value={form.nmi} onChange={function(e) { updateForm('nmi', e.target.value); }} className="w-full h-11 px-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="Found on your bill (e.g., 6102000001)" data-testid="customer-nmi" />
                <p className="text-xs text-slate-400 mt-1">Optional — helps us find the best rate for your area</p>
              </div>
            </div>
            <button onClick={submitDetails} disabled={loading} className="w-full h-12 bg-primary text-white rounded-full font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid="customer-next-btn">
              {loading ? 'Please wait...' : 'Next \u2192'}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white rounded-xl border shadow-sm p-6 space-y-5" data-testid="customer-step-2">
            <div className="text-center mb-4">
              <h1 className="text-2xl font-bold text-slate-900">Upload Your Bill</h1>
              <p className="text-slate-500 mt-1">Upload a PDF of your current electricity bill</p>
            </div>
            <div onClick={function() { document.getElementById('customer-file-input').click(); }} className="border-2 border-dashed rounded-xl p-10 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors">
              <input id="customer-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.tiff,.tif,.bmp,.heic" className="hidden" onChange={function(e) { if (e.target.files[0]) setFile(e.target.files[0]); }} />
              <div className="text-4xl text-slate-300 mb-3">{file ? '\u2705' : '\ud83d\udcc4'}</div>
              <p className="text-lg font-medium text-slate-900">{file ? file.name : 'Click to upload your bill'}</p>
              <p className="text-sm text-slate-500 mt-1">PDF format only</p>
            </div>
            {file && (
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="text-green-600 font-medium flex-1">{file.name}</span>
                <button onClick={function() { setFile(null); }} className="text-sm text-slate-500 hover:text-red-500">Remove</button>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={function() { setStep(1); }} className="px-6 h-12 border rounded-full font-medium hover:bg-slate-50">{'\u2190'} Back</button>
              <button onClick={uploadBill} disabled={!file || loading} className="flex-1 h-12 bg-primary text-white rounded-full font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid="customer-upload-btn">
                {loading ? 'Processing your bill...' : 'Analyse My Bill \u2192'}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5" data-testid="customer-step-3">
            <div className="bg-white rounded-xl border shadow-sm p-6">
              <div className="text-center mb-5">
                <h1 className="text-2xl font-bold text-slate-900">Select Your Plan</h1>
                <p className="text-slate-500 mt-1">{detectedState ? 'We detected your state as ' + detectedState + '. ' : ''}Choose a tariff and plan below.</p>
              </div>

              {tariffs.length > 1 && (
                <div className="mb-5">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Tariff</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {tariffs.map(function(t) {
                      var sel = selectedTariff === t.id;
                      return (
                        <button key={t.id} onClick={function() { onTariffSelect(t.id); }} className={'p-4 rounded-xl border-2 text-left transition-all ' + (sel ? 'border-primary bg-primary/5 shadow-md' : 'border-slate-200 hover:border-slate-300')} data-testid={'tariff-' + t.id}>
                          <div className="font-semibold text-slate-900">{t.tariff_code}</div>
                          <div className="text-sm text-slate-500">{t.tariff_name}</div>
                          {t.state && <span className="inline-block mt-1 text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{t.state}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {tariffs.length === 1 && (
                <div className="mb-5 p-3 bg-slate-50 rounded-lg border text-sm">
                  <span className="font-medium">Tariff: </span>{tariffs[0].tariff_code} - {tariffs[0].tariff_name}
                </div>
              )}

              {plans.length > 0 && (
                <div className="mb-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Choose your discount plan</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {plans.map(function(p) {
                      var sel = selectedPlan === p.id;
                      return (
                        <button key={p.id} onClick={function() { setSelectedPlan(p.id); }} className={'p-5 rounded-xl border-2 text-center transition-all ' + (sel ? 'border-primary bg-primary/5 shadow-md ring-2 ring-primary/20' : 'border-slate-200 hover:border-slate-300 hover:shadow-sm')} data-testid={'plan-' + p.id}>
                          <div className="text-3xl font-bold text-primary mb-1">{p.discount_percentage}%</div>
                          <div className="font-semibold text-slate-900 text-sm">{p.plan_name}</div>
                          {p.description && <div className="text-xs text-slate-500 mt-1">{p.description}</div>}
                          {p.conditions && <div className="text-xs text-amber-600 mt-1">{p.conditions}</div>}
                          <div className="text-xs text-slate-400 mt-2">{p.discount_scope === 'usage_only' ? 'Usage only' : 'Usage & Supply'}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {plans.length === 0 && !loading && (
                <div className="text-center py-6 text-slate-500">No plans available for this tariff.</div>
              )}
            </div>

            <div className="flex gap-3">
              <button onClick={function() { setStep(2); }} className="px-6 h-12 border rounded-full font-medium hover:bg-slate-50">{'\u2190'} Back</button>
              <button onClick={runComparison} disabled={!selectedTariff || !selectedPlan || loading} className="flex-1 h-12 bg-primary text-white rounded-full font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid="customer-compare-btn">
                Compare Now {'\u2192'}
              </button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="bg-white rounded-xl border shadow-sm p-8 text-center" data-testid="customer-step-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
            <h2 className="text-xl font-bold text-slate-900">Analysing Your Bill</h2>
            <p className="text-slate-500 mt-2">We're comparing your current rates against our best offer...</p>
          </div>
        )}

        {step === 5 && result && (
          <div className="space-y-5" data-testid="customer-step-5">
            <div className="bg-white rounded-xl border shadow-sm p-6 text-center">
              <p className="text-sm text-slate-500 mb-1">Your Estimated Savings</p>
              <p className={'text-5xl font-bold ' + ((result.result?.savings_amount || 0) > 0 ? 'text-green-600' : 'text-amber-600')} data-testid="customer-savings">{fmt(Math.abs(result.result?.savings_amount || 0))}</p>
              <p className="text-slate-600 mt-1">{(result.result?.savings_amount || 0) > 0 ? 'less' : 'more'} per quarter ({Math.abs(result.result?.savings_percentage || 0).toFixed(1)}%)</p>
              <p className="text-sm text-primary font-medium mt-2">Projected annual savings: {fmt(Math.abs(result.result?.savings_amount || 0) * 4)}/year</p>
            </div>

            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <div className="p-4 border-b bg-slate-50">
                <h3 className="font-semibold text-slate-900">Cost Comparison</h3>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="border-b bg-slate-50/50"><th className="text-left py-3 px-4 font-medium text-slate-500">Charge</th><th className="text-right py-3 px-4 font-medium text-slate-500">Your Current</th><th className="text-right py-3 px-4 font-medium text-primary">Our Offer</th></tr></thead>
                <tbody>
                  <tr className="border-b"><td className="py-3 px-4">Supply Charges</td><td className="text-right py-3 px-4 font-mono">{fmt(result.result?.retailer_supply_cost)}</td><td className="text-right py-3 px-4 font-mono text-primary">{fmt(result.result?.our_supply_cost)}</td></tr>
                  <tr className="border-b"><td className="py-3 px-4">Usage Charges</td><td className="text-right py-3 px-4 font-mono">{fmt(result.result?.retailer_usage_cost)}</td><td className="text-right py-3 px-4 font-mono text-primary">{fmt(result.result?.our_usage_cost)}</td></tr>
                  <tr className="border-b"><td className="py-3 px-4">Discounts</td><td className="text-right py-3 px-4 font-mono text-green-600">-{fmt(result.result?.retailer_discount)}</td><td className="text-right py-3 px-4 font-mono text-green-600">-{fmt(result.result?.our_discount)}</td></tr>
                  <tr className="bg-slate-50 font-semibold"><td className="py-3 px-4">Total</td><td className="text-right py-3 px-4 font-mono">{fmt(result.result?.retailer_total)}</td><td className="text-right py-3 px-4 font-mono text-primary">{fmt(result.result?.our_total)}</td></tr>
                </tbody>
              </table>
            </div>

            <div className="bg-white rounded-xl border shadow-sm p-5">
              <h3 className="font-semibold text-slate-900 mb-2">Your Details</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-slate-500">Customer</span><span className="font-medium">{result.customer_data?.customer_name || form.name}</span>
                <span className="text-slate-500">NMI</span><span className="font-medium font-mono">{result.customer_data?.nmi || form.nmi || 'N/A'}</span>
                <span className="text-slate-500">Current Retailer</span><span className="font-medium">{result.customer_data?.retailer_name || 'N/A'}</span>
                <span className="text-slate-500">Tariff</span><span className="font-medium">{result.tariff_name}</span>
                <span className="text-slate-500">Plan</span><span className="font-medium">{result.plan_name}</span>
              </div>
            </div>

            <div className="bg-primary/5 rounded-xl border border-primary/20 p-5 text-center">
              <h3 className="font-semibold text-primary mb-1">Interested in switching?</h3>
              <p className="text-sm text-slate-600 mb-3">One of our team will be in touch to discuss your options.</p>
              <p className="text-xs text-slate-400">Reference: {result.report_id}</p>
            </div>
          </div>
        )}
      </div>
      )}
      </div>

      <PublicFooter />
    </div>
  );
}
